import type { RouteRecordRaw } from 'vue-router'

import { registerMarketingInsightsMessages } from './i18n'

import { registerReportMessages } from '@/features/reports'
import { DEFAULT_LOCALE, SUPPORTED_LOCALES, i18n, type SupportedLocale } from '@/shared/i18n'

export const TREND_SEASONALITY_ROUTE = { name: 'TrendSeasonalityAnalysis' } as const
export const COMPETITOR_ANALYSIS_ROUTE = { name: 'CompetitorAnalysis' } as const
export const PERFORMANCE_MAX_ROUTE = { name: 'PerformanceMaxCampaigns' } as const

function activeLocale(): SupportedLocale {
  const locale = i18n.global.locale.value
  return (SUPPORTED_LOCALES as readonly string[]).includes(locale)
    ? (locale as SupportedLocale)
    : DEFAULT_LOCALE
}

async function prepareConfigDrivenRoute(): Promise<void> {
  const locale = activeLocale()
  const [{ registerMarketingInsightsLayouts }] = await Promise.all([
    import('./flows/registerMarketingInsightsLayouts'),
    registerReportMessages(locale),
    registerMarketingInsightsMessages(locale),
  ])
  registerMarketingInsightsLayouts()
}

async function prepareBespokeRoute(): Promise<void> {
  const locale = activeLocale()
  await Promise.all([registerReportMessages(locale), registerMarketingInsightsMessages(locale)])
}

export const marketingInsightsRoutes: RouteRecordRaw[] = [
  // ORP-18435 — the Meta and TikTok explorer pages are retired: their Campaign and Ad Group tabs
  // drew the same data as Budget Allocation with fewer controls (no cross-platform switcher, no Ad
  // or Key Events level, no Actual vs Previous), and the platform-native breakdowns they exposed as
  // raw tables are now produced automatically in Insights Center. 15 sessions in three months.
  //
  // These stay as NAMED redirects rather than being deleted outright. The names are load-bearing:
  // `ingress/__fixtures__/v3Routes.ts` probes both paths as live v3 URLs, and the coverage spec
  // requires every v3 URL to resolve to a named route rather than `not-found` — a bookmark or a
  // deep link must land somewhere deliberate, not on the 404. `to.query` is forwarded whole, which
  // is what carries the date range across; with no query, Budget Allocation applies its own default.
  //
  // NOT done here, and deliberately: the ticket also asks for the destination's platform switcher to
  // arrive preselected on Meta / TikTok. That selection is component state seeded from a cache
  // (`reports/composables/useSectionSelections.ts:78-83`) and reads nothing from the URL, so honouring
  // it means giving the shared rail a URL-addressable selection — a change to every report page with
  // a rail. Tracked separately; the redirect below already removes the dead-page landing, which is
  // the harm the ticket exists to prevent.
  {
    path: 'marketing-insights/paid-social/meta-ad-insight',
    name: 'MetaAdAnalysis',
    redirect: (to) => ({ name: 'BudgetAllocation', query: to.query }),
  },
  {
    path: 'marketing-insights/paid-social/tiktok-analyses',
    name: 'TiktokAnalyses',
    redirect: (to) => ({ name: 'BudgetAllocation', query: to.query }),
  },
  {
    path: 'marketing-insights/search-marketing/display-video-360',
    name: 'DisplayVideo360',
    component: () => import('./components/pages/DisplayVideo360Page.vue'),
    meta: {
      permission: 'report:43',
      navKey: 'displayVideo360',
      titleKey: 'marketingInsights.displayVideo360.title',
    },
    beforeEnter: prepareConfigDrivenRoute,
  },
  {
    path: 'marketing-insights/search-marketing/search-ads-excellence',
    name: 'SearchAdsExcellence',
    component: () => import('./components/pages/SearchAdsExcellencePage.vue'),
    meta: {
      permission: 'report:1',
      navKey: 'searchAdsExcellence',
      titleKey: 'marketingInsights.searchAdsExcellence.title',
    },
    beforeEnter: prepareConfigDrivenRoute,
  },
  {
    path: 'marketing-insights/trending-competition/competitor-analysis',
    name: COMPETITOR_ANALYSIS_ROUTE.name,
    component: () => import('./components/pages/CompetitorAnalysisPage.vue'),
    meta: {
      permission: 'report:47',
      navKey: 'competitorAnalysis',
      titleKey: 'marketingInsights.competitorAnalysis.title',
    },
    beforeEnter: prepareBespokeRoute,
  },
  {
    path: 'marketing-insights/trending-competition/trend-seasonality-analysis',
    name: TREND_SEASONALITY_ROUTE.name,
    component: () => import('./components/pages/TrendSeasonalityPage.vue'),
    meta: {
      permission: 'trend_analysis',
      navKey: 'trendSeasonality',
      titleKey: 'marketingInsights.trendSeasonality.title',
    },
    beforeEnter: prepareBespokeRoute,
  },
  {
    path: 'marketing-insights/performance-max-campaigns',
    name: PERFORMANCE_MAX_ROUTE.name,
    component: () => import('./components/pages/PerformanceMaxPage.vue'),
    meta: {
      permission: 'report:26',
      navKey: 'performanceMax',
      titleKey: 'marketingInsights.performanceMax.title',
    },
    beforeEnter: prepareConfigDrivenRoute,
  },
  {
    path: 'marketing-insights/landing-page',
    name: 'LandingPage',
    component: () => import('./components/pages/LandingPageReportPage.vue'),
    meta: {
      permission: 'report:22',
      navKey: 'landingPage',
      titleKey: 'marketingInsights.landingPage.title',
    },
    // Retired report (ORP-24296): the page renders no report, so only copy is registered.
    beforeEnter: prepareBespokeRoute,
  },
  {
    path: 'marketing-insights/placement-analysis',
    name: 'PlacementAnalysis',
    component: () => import('./components/pages/PlacementAnalysisPage.vue'),
    meta: {
      permission: 'report:60',
      navKey: 'placementAnalysis',
      titleKey: 'marketingInsights.placementAnalysis.title',
    },
    // Retired report (ORP-24296): the page renders no report, so only copy is registered.
    beforeEnter: prepareBespokeRoute,
  },
]
